import React from 'react';
import { Alert, Button } from 'react-bootstrap';

interface ErrorMessageProps {
  message?: string;
  messages?: string[];
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  showRetry?: boolean;
  showDismiss?: boolean;
  variant?: string;
}

/**
 * A reusable error message component that can display an error message or array of error messages
 * with optional retry and dismiss buttons.
 * 
 * @param message - The error message to display (single message)
 * @param messages - Array of error messages to display
 * @param title - Optional title for the error message
 * @param onRetry - Optional callback function to retry the operation
 * @param onDismiss - Optional callback function to dismiss the error
 * @param showRetry - Whether to show the retry button
 * @param showDismiss - Whether to show the dismiss button
 * @param variant - Bootstrap variant for the alert (default: 'danger')
 */
const ErrorMessage: React.FC<ErrorMessageProps> = ({
  message,
  messages,
  title = 'Error',
  onRetry,
  onDismiss,
  showRetry = false,
  showDismiss = true,
  variant = 'danger'
}) => {
  // Determine what messages to show - either the single message or array of messages
  const errorMessages = messages || (message ? [message] : []);

  return (
    <Alert variant={variant} className="mb-4">
      <Alert.Heading>{title}</Alert.Heading>
      
      {errorMessages.length === 1 ? (
        <p>{errorMessages[0]}</p>
      ) : (
        <ul className="mb-0">
          {errorMessages.map((msg, index) => (
            <li key={index}>{msg}</li>
          ))}
        </ul>
      )}
      
      <div className="mt-3 border-top pt-2">
        <p className="small text-muted">
          Experiencing issues? <a href="https://issues.amazon.com/issues/create?template=01e23cf1-7eb1-4b95-9316-2dc9ed7795c6" target="_blank" rel="noopener noreferrer">Submit a SIM</a>
        </p>
      </div>
      
      {(showRetry || showDismiss) && (
        <div className="d-flex justify-content-end mt-3">
          {showRetry && onRetry && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={onRetry}
              className="me-2"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Retry
            </Button>
          )}
          
          {showDismiss && onDismiss && (
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={onDismiss}
            >
              <i className="bi bi-x-circle me-1"></i>
              Dismiss
            </Button>
          )}
        </div>
      )}
    </Alert>
  );
};

export default ErrorMessage;
